import { Field, TextField, ActionButton, Button, Text, Checkbox } from 'components/commons';
import { useApi, useForm, useModal } from 'hooks';
import React, { useCallback, useMemo, useState } from 'react';
import { initialState as formState } from './transaction-entry-form.state';
import styles from './transaction-entry.module.scss';
import { NotifModal } from 'components/modals';
import { CircularProgress, Grid } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import locale from 'localization';
import { CarWithLady, LucbanGenesisLogo } from 'images';
import { formatAmount, handleRequest, parseNumber } from 'utils';
import SelectAccount from 'components/field/select-account/select-account';
import DatePicker from 'components/commons/datetime/date-picker';
import { InputAmount } from 'components/field';
import { createBatchTransaction, getLatestCashVoucherTransaction } from 'apis/transaction.api';
import * as moment from 'moment';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { dateTimeFormatC } from 'utils/date.utils';
import { numberToWords } from 'utils/text.utils';
import Big from 'big.js';
import TransactionEntryMembersModal from './transaction-entry-members-modal';

const TransactionEntryForm = ({ initialState, actionText, create, accountId, refreshPage, ...state }) => {
  const [rowMembers, setRowMembers] = useState([
    { accountId: 329, members: [], totalAmount: 0 },
    { accountId: 402, members: [], totalAmount: 0 }
  ]);

  const voucherData = {
    preparedBy: `${localStorage?.getItem('firstName')?.toUpperCase() || ''} ${
      localStorage?.getItem('lastName')?.toUpperCase() || ''
    }`,
    checkedBy: 'ANA RIZA DIWATA\nGeneral Manager',
    approvedBy: 'RAMIL GAVIÑO\nChairman'
  };

  const [debits, setDebits] = useState([
    { label: '', accountId: '', amount: '', orNumber: '', code: '', name: '', isCash: false }
  ]);
  const [credits, setCredits] = useState([
    { label: '', laccountId: '', amount: '', cashVoucherNumber: '', code: '', name: '', isCash: false }
  ]);

  const notifModal = useModal();
  const membersModal = useModal();
  const form = useMemo(() => {
    return formState(initialState);
  }, [initialState]);

  const { fields, modifyField, validateField, modifyForm } = useForm({
    initialState: form
  });

  const createTransactionRequest = useApi({
    api: createBatchTransaction,
    handleOwnError: {
      badrequest: true
    }
  });

  const getLatestCashVoucherRequest = useApi({
    api: getLatestCashVoucherTransaction,
    handleOwnError: {
      badrequest: false
    }
  });

  const generateNextCashVoucherNumber = async () => {
    const today = moment().format('YYYYMMDD');

    const res = await getLatestCashVoucherRequest.request({ date: today }); // pass today's date if needed
    let nextNumber = 1;

    if (res?.cashVoucherNumber) {
      const latest = res.cashVoucherNumber;
      const parts = latest.split('-');
      if (parts.length === 2) {
        nextNumber = parseInt(parts[1], 10) + 1;
      }
    }

    const formattedNumber = String(nextNumber).padStart(6, '0');
    return `${today}-${formattedNumber}`;
  };

  const reset = useCallback(() => {
    setDebits([{ label: '', accountId: '', amount: '', orNumber: '', code: '', name: '', isCash: false }]);
    setCredits([{ label: '', accountId: '', amount: '', cashVoucherNumber: '', code: '', name: '', isCash: false }]);
    modifyForm({
      orNumber: { value: '' },
      remarks: { value: '' },
      transactedAt: { value: { startDate: null } }
    });

    setRowMembers([
      { accountId: 329, members: [], totalAmount: 0 },
      { accountId: 402, members: [], totalAmount: 0 }
    ]);
  }, [modifyForm]);

  const hasCashVoucher = credits.some((c) => c.cashVoucherNumber && c.cashVoucherNumber.trim());

  const printPdf = useCallback(() => {
    const cashVoucherItem = credits.find((c) => c.cashVoucherNumber && c.cashVoucherNumber.trim());

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    const marginLeft = 10;
    const marginRight = 10;
    const pageWidth = 210 - marginLeft - marginRight;

    const amount = parseNumber(cashVoucherItem.amount);
    const formattedAmount = formatAmount(amount, null);
    const amountInWords = numberToWords(amount);

    // Header Box
    doc.setFontSize(10);

    doc.setFont('helvetica', 'bold');
    // Add Logo
    doc.addImage(LucbanGenesisLogo, 'PNG', 30, 5, 30, 30);
    doc.text('LUCBAN GENESIS TRANSPORT SERVICE & MPC', marginLeft + pageWidth / 2 + 2, 12, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.text('CDA Registration No. 9520-04009811', marginLeft + pageWidth / 2 + 2, 17, { align: 'center' });
    doc.text('OTC Accreditation No. 2011-265', marginLeft + pageWidth / 2 + 2, 22, { align: 'center' });

    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('CASH VOUCHER', marginLeft + pageWidth / 2, 35, { align: 'center' });

    // Date right-aligned above amount
    doc.setFontSize(15);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(255, 0, 0);
    doc.text(`No: ${cashVoucherItem.cashVoucherNumber}`, marginLeft + pageWidth - 2, 35, { align: 'right' });
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`Date: ${dateTimeFormatC(fields.transactedAt?.value?.startDate)}`, marginLeft + pageWidth - 2, 45, {
      align: 'right'
    });
    doc.setFont('helvetica', 'normal');
    // Particulars Table
    autoTable(doc, {
      startY: 48,
      head: [['PARTICULARS', 'AMOUNT']],
      body: [[fields.remarks.value, `Php ${formattedAmount}`]],
      margin: { left: marginLeft, right: marginRight },
      styles: {
        fontSize: 10,
        halign: 'left',
        lineColor: 0,
        lineWidth: 0.2,
        cellPadding: 0.5
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: 0,
        lineColor: 0,
        lineWidth: 0.2,
        fontSize: 10,
        halign: 'center'
      },
      bodyStyles: {
        fontSize: 10
      },
      columnStyles: {
        0: { cellWidth: pageWidth - 80, halign: 'center', valign: 'middle' },
        1: { cellWidth: 80, halign: 'center', valign: 'middle' }
      },
      theme: 'grid',
      didParseCell: function (data) {
        if (data.section === 'body' && data.column.index === 1) {
          data.cell.styles.fontSize = 15;
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.textColor = [0, 0, 0]; // Red color for amount
          data.cell.styles.cellPadding = 2;
        }
      }
    });

    // Main Doc Table
    const tableStartY = doc.lastAutoTable.finalY + 5;
    const totalDebits = debits.reduce((sum, item) => sum + parseNumber(item.amount || 0), 0);
    const totalCredits = credits.reduce((sum, item) => sum + parseNumber(item.amount || 0), 0);

    const tableData = [
      ...debits.map((item) => [
        item.code || '',
        item.name || '',
        item.amount ? formatAmount(parseNumber(item.amount), null) : '',
        '',
        ''
      ]),
      ...credits.map((item) => [
        item.code || '',
        item.name || '',
        '',
        item.amount ? formatAmount(parseNumber(item.amount), null) : '',
        ''
      ]),
      [
        'TOTAL',
        '',
        totalDebits ? formatAmount(totalDebits, null) : '',
        totalCredits ? formatAmount(totalCredits, null) : '',
        fields.payee.value || 'EMPLOYEE'
      ]
    ];

    autoTable(doc, {
      startY: tableStartY,
      head: [['CODE', 'ACCOUNT TITLE', 'DEBIT', 'CREDIT', 'PAYEE']],
      body: tableData,
      margin: { left: marginLeft, right: marginRight },
      styles: {
        fontSize: 10,
        halign: 'center',
        lineColor: 0,
        lineWidth: 0.2,
        cellPadding: 0.5
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: 0,
        lineColor: 0,
        lineWidth: 0.2,
        fontSize: 10
      },
      bodyStyles: {
        fontSize: 10
      },
      columnStyles: {
        0: { cellWidth: 25, valign: 'middle', halign: 'center' },
        1: { cellWidth: 70, valign: 'middle', halign: 'center' },
        2: { cellWidth: 30, valign: 'middle', halign: 'center' },
        3: { cellWidth: 30, valign: 'middle', halign: 'center' },
        4: { cellWidth: 35, valign: 'middle', halign: 'center' }
      },
      theme: 'grid',
      didParseCell: function (data) {
        // Make last row bold
        const lastRowIndex = tableData.length - 1;
        if (data.row.index === lastRowIndex) {
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });

    const wordsY = doc.lastAutoTable.finalY + 10;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Amount in words:', marginLeft + 2, wordsY);

    const wordsLabelWidth = doc.getTextWidth('Amount in words: ') + 1;
    doc.text(amountInWords, marginLeft + 2 + wordsLabelWidth, wordsY);

    // Underline only the value
    const underlineStartX = marginLeft + 2 + wordsLabelWidth;
    const underlineEndX = marginLeft + pageWidth;
    doc.line(underlineStartX, wordsY + 1, underlineEndX, wordsY + 1);

    // Signature Line (Approved by)
    const sigY = wordsY + 25;

    // Signature Over Printed Name for Approved By (right-aligned)
    doc.setDrawColor(0);
    // Draw underline
    doc.line(marginLeft + (pageWidth * 2) / 3, sigY, pageWidth + 10, sigY); // full-width underline

    // Signature label below line
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text('Signature over Printed Name', marginLeft + pageWidth - 6, sigY + 4, { align: 'right' });

    // Signatories Table
    autoTable(doc, {
      startY: sigY + 10,
      head: [['Prepared By', 'Checked By', 'Approved By']],
      body: [
        [`\n\n${voucherData.preparedBy || ''}`, `\n\n${voucherData.checkedBy || ''}`, `\n\n${voucherData.approvedBy || ''}`]
      ],
      margin: { left: marginLeft, right: marginRight },
      styles: {
        fontSize: 10,
        halign: 'center',
        valign: 'middle',
        lineColor: 0,
        lineWidth: 0.2,
        cellPadding: 0.5
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: 0,
        lineWidth: 0.2,
        fontSize: 10
      },
      columnStyles: {
        0: { cellWidth: pageWidth / 3 },
        1: { cellWidth: pageWidth / 3 },
        2: { cellWidth: pageWidth / 3 }
      },
      theme: 'grid',
      didParseCell: function (data) {
        const lastRowIndex = 1; // The second row (index 1) is the one with "General Manager", "Chairman"

        if (data.row.index === lastRowIndex) {
          // Remove top border
          data.cell.styles.lineWidth = { top: 0, right: 0.2, bottom: 0.2, left: 0.2 };
          // Make font bold
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });

    // Final Output
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);
    window.open(url);
  }, [
    credits,
    debits,
    fields.payee.value,
    fields.remarks.value,
    fields.transactedAt?.value?.startDate,
    voucherData.approvedBy,
    voucherData.checkedBy,
    voucherData.preparedBy
  ]);

  const submit = useCallback(async () => {
    handleRequest(async () => {
      const data = [];
      const transactionCustomersData = [];

      for (const debit of debits) {
        data.push({
          transactedAt: fields.transactedAt?.value?.startDate,
          accountId: debit.accountId,
          amount: parseNumber(debit.amount),
          type: 'debit',
          orNumber: debit.orNumber,
          userId: Number(localStorage.getItem('userId')),
          remarks: fields.remarks.value,
          payee: fields.payee.value,
          isGeneralJournal: fields.isGeneralJournal.value
        });
      }

      for (const credit of credits) {
        data.push({
          transactedAt: fields.transactedAt?.value?.startDate,
          accountId: credit.accountId,
          amount: parseNumber(credit.amount),
          type: 'credit',
          cashVoucherNumber: credit.cashVoucherNumber,
          userId: Number(localStorage.getItem('userId')),
          remarks: fields.remarks.value,
          payee: fields.payee.value,
          isGeneralJournal: fields.isGeneralJournal.value
        });
      }

      for (const r of rowMembers) {
        if (r.members && r.members.length > 0) {
          for (const m of r.members) {
            transactionCustomersData.push({
              customerId: m.customerId,
              customerAccountNumber: m.customerAccountNumber,
              customerAccountId: m.customerAccountId,
              lastName: m.lastName,
              firstName: m.firstName,
              amount: parseNumber(m.amount),
              type: m.type,
              accountId: r.accountId
            });
          }
        }
      }

      const finalParams = {
        data,
        transactionCustomersData
      };

      await createTransactionRequest.request(finalParams);

      if (!fields.isGeneralJournal.value && hasCashVoucher) {
        printPdf();
        notifModal.show({
          image: CarWithLady,
          title: 'Transaction Entry Success',
          content: 'The transaction entry has been submitted. A cash voucher has been created and is ready for printing.',
          secondary: {
            text: 'Print Cash Voucher',
            onClick: printPdf
          }
        });
      } else {
        notifModal.show({
          image: CarWithLady,
          title: 'Transaction Entry Success',
          content: 'Successfully created transaction enries',
          secondary: null
        });
      }

      reset();
    });
  }, [
    createTransactionRequest,
    credits,
    debits,
    fields.isGeneralJournal.value,
    fields.payee.value,
    fields.remarks.value,
    fields.transactedAt?.value?.startDate,
    hasCashVoucher,
    notifModal,
    printPdf,
    reset,
    rowMembers
  ]);

  const handleCreditChange = async (index, field, value, label = '', code, name, isCash = false) => {
    if (field === 'accountId') {
      // Check for duplicate accountId
      const duplicate = credits.some((item, i) => item.accountId === value && i !== index);
      if (duplicate) {
        alert('This account has already been selected in credits.');
        return;
      }

      if ([402, 329].includes(value)) {
        const existsInDebits = debits.some((d) => d.accountId === value);
        if (existsInDebits) {
          alert(`Account cannot be selected in both debit and credit.`);
          return;
        }
      }

      if (isCash) {
        const hasCashDebit = debits.some((item) => item.isCash);
        if (hasCashDebit) {
          alert('You cannot select a cash account in both debit and credit.');
          return;
        }
      }

      let cashVoucherNumber = '';
      if (isCash) {
        cashVoucherNumber = await generateNextCashVoucherNumber();
      }

      // 🧹 Reset members if accountId changes
      setRowMembers((prev) =>
        prev.map((r) => (r.accountId === credits[index].accountId ? { ...r, members: [], totalAmount: 0 } : r))
      );

      setCredits((prev) =>
        prev.map((item, i) =>
          i === index
            ? { ...item, accountId: value, label, code, name, isCash, cashVoucherNumber, amount: '' } // also reset amount
            : item
        )
      );
    } else {
      setCredits((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
    }
  };

  const handleDebitChange = (index, field, value, label = '', code, name, isCash = false) => {
    if (field === 'accountId') {
      // Check for duplicate accountId
      const duplicate = debits.some((item, i) => item.accountId === value && i !== index);
      if (duplicate) {
        alert('This account has already been selected in debits.');
        return;
      }

      if ([402, 329].includes(value)) {
        const existsInCredits = credits.some((c) => c.accountId === value);
        if (existsInCredits) {
          alert(`Account cannot be selected in both debit and credit.`);
          return;
        }
      }

      if (isCash) {
        const anotherCashExists = debits.some((item, i) => item.isCash && i !== index);
        if (anotherCashExists) {
          alert('Only one cash account can be selected in debits.');
          return;
        }

        const hasCashCredit = credits.some((item) => item.isCash);
        if (hasCashCredit) {
          alert('You cannot select a cash account in both debit and credit.');
          return;
        }

        modifyForm({
          transactedAt: { value: { startDate: null } }
        });
      }

      setRowMembers((prev) =>
        prev.map((r) => (r.accountId === debits[index].accountId ? { ...r, members: [], totalAmount: 0 } : r))
      );

      setDebits((prev) =>
        prev.map((item, i) =>
          i === index
            ? { ...item, accountId: value, label, code, name, isCash, amount: '' } // also reset amount
            : item
        )
      );
    } else {
      setDebits((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
    }
  };

  const addDebitRow = () => {
    setDebits([...debits, { accountId: '', amount: '' }]);
  };

  const addCreditRow = () => {
    setCredits([...credits, { accountId: '', amount: '' }]);
  };

  const removeDebitRow = (index) => {
    const updated = [...debits];
    updated.splice(index, 1);
    setDebits(updated);
  };

  const removeCreditRow = (index) => {
    const updated = [...credits];
    updated.splice(index, 1);
    setCredits(updated);
  };

  const getTotal = (entries) => entries.reduce((sum, entry) => sum + (parseNumber(entry.amount) || 0), 0);

  const hasValues = (entries) => entries.some((entry) => entry.amount !== '' && entry.accountId !== '');
  const totalDebits = getTotal(debits);
  const totalCredits = getTotal(credits);
  const isBalanced = Big(totalDebits.toFixed(2)).eq(totalCredits.toFixed(2));

  const shouldShowBalanceMessage = hasValues(debits) || hasValues(credits);

  const areEntriesComplete = (entries) => entries.every((entry) => entry.accountId);

  const entriesAreComplete = areEntriesComplete(debits) && areEntriesComplete(credits);

  const isTransactedAtValid = !!fields.transactedAt.value.startDate;

  const orNumbersValid = debits.every((d) => !d.isCash || d.orNumber?.trim());
  const cashVoucherNumbersValid = credits.every((c) => !c.isCash || c.cashVoucherNumber?.trim());

  const isCashCredit = credits.some((d) => d.isCash);
  const isCashDebit = debits.some((d) => d.isCash);
  const payeeValid = !isCashCredit || (fields.payee?.value && fields.payee.value.trim() !== '');

  return (
    <div>
      <TransactionEntryMembersModal {...membersModal} />
      <NotifModal {...notifModal} />

      <div className={styles.formContent}>
        <div className={styles.content}>
          <Grid container direction="row" spacing={2}>
            <Grid item xs={12} sm={3}>
              <Field label={'Document Date'}>
                <DatePicker
                  {...fields.transactedAt}
                  onChange={modifyField}
                  isCurrentDateMax={isCashDebit && !fields.isGeneralJournal.value}
                />
              </Field>
            </Grid>
          </Grid>

          {debits.map((debit, index) => (
            <Grid container spacing={2} key={`debit-${index}`} alignItems="center">
              <Grid item xs={12} sm={3}>
                <Field label={'Debit'}>
                  <SelectAccount
                    value={debit.accountId}
                    label={debit.label}
                    onChange={(name, { value, label, isCash, code, accountName }) =>
                      handleDebitChange(index, 'accountId', value, label, code, accountName, isCash)
                    }
                    isPostable
                  />
                </Field>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Field label={'Amount'}>
                  <InputAmount
                    value={debit.amount}
                    onChange={(name, { value, label }) => handleDebitChange(index, 'amount', value)}
                    disabled={[402, 329].includes(debit.accountId)}
                  />
                </Field>
              </Grid>
              {!fields.isGeneralJournal.value && debit.isCash && (
                <Grid item xs={12} sm={3}>
                  <Field label="OR Number">
                    <TextField
                      value={debit.orNumber}
                      onChange={(name, { value }) => handleDebitChange(index, 'orNumber', value)}
                    />
                  </Field>
                </Grid>
              )}
              {[329, 402].includes(debit.accountId) && (
                <Grid item xs={12} sm={3}>
                  <Button
                    link
                    onClick={() => {
                      console.log(
                        'Opening members modal for accountId:',
                        rowMembers.find((r) => r.accountId === debit.accountId)
                      );
                      membersModal.show({
                        accountId: debit.accountId,
                        members: rowMembers.find((r) => r.accountId === debit.accountId)?.members || [],
                        type: 'debit',
                        setMembers: (updatedMembers, totalAmount) => {
                          setRowMembers((prev) => {
                            const exists = prev.some((r) => r.accountId === debit.accountId);
                            if (exists) {
                              return prev.map((r) =>
                                r.accountId === debit.accountId ? { ...r, members: updatedMembers, totalAmount } : r
                              );
                            } else {
                              return [...prev, { accountId: debit.accountId, members: updatedMembers, totalAmount }];
                            }
                          });

                          console.log(rowMembers);

                          setDebits((prev) =>
                            prev.map((d, i) => (i === index ? { ...d, amount: formatAmount(totalAmount, null) } : d))
                          );
                        }
                      });
                    }}
                    className={styles.memberLabel}
                  >
                    Members ({rowMembers.find((r) => r.accountId === debit.accountId)?.members.length || 0})
                  </Button>
                </Grid>
              )}
              <Grid item xs={12} sm={1}>
                {debits.length > 1 && (
                  <Button className={styles.remove} link onClick={() => removeDebitRow(index)}>
                    <CloseIcon />
                  </Button>
                )}
              </Grid>
            </Grid>
          ))}

          <Button link startIcon={<AddIcon />} onClick={addDebitRow}>
            Add Debit
          </Button>

          <br />
          <br />

          {credits.map((credit, index) =>
            !getLatestCashVoucherRequest.loading ? (
              <Grid container spacing={2} key={`credit-${index}`} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <Field label={'Credit'}>
                    <SelectAccount
                      value={credit.accountId}
                      label={credit.label}
                      onChange={(name, { value, label, isCash, code, accountName }) =>
                        handleCreditChange(index, 'accountId', value, label, code, accountName, isCash)
                      }
                      isPostable
                    />
                  </Field>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Field label={'Amount'}>
                    <InputAmount
                      value={credit.amount}
                      disabled={[402, 329].includes(credit.accountId)}
                      onChange={(name, { value }) => {
                        handleCreditChange(index, 'amount', value);
                      }}
                    />
                  </Field>
                </Grid>
                {!fields.isGeneralJournal.value && credit.isCash && (
                  <Grid item xs={12} sm={3}>
                    <Field label="Cash Voucher Number">
                      <TextField
                        value={credit.cashVoucherNumber}
                        disabled
                        onChange={(name, { value }) => handleCreditChange(index, 'cashVoucherNumber', value)}
                      />
                    </Field>
                  </Grid>
                )}
                {[329, 402].includes(credit.accountId) && (
                  <Grid item xs={12} sm={3}>
                    <Button
                      link
                      onClick={() => {
                        console.log(
                          'Opening members modal for accountId:',
                          rowMembers.find((r) => r.accountId === credit.accountId)
                        );
                        membersModal.show({
                          accountId: credit.accountId,
                          members: rowMembers.find((r) => r.accountId === credit.accountId)?.members || [],
                          type: 'credit',
                          setMembers: (updatedMembers, totalAmount) => {
                            setRowMembers((prev) => {
                              const exists = prev.some((r) => r.accountId === credit.accountId);
                              if (exists) {
                                return prev.map((r) =>
                                  r.accountId === credit.accountId ? { ...r, members: updatedMembers, totalAmount } : r
                                );
                              } else {
                                return [...prev, { accountId: credit.accountId, members: updatedMembers, totalAmount }];
                              }
                            });

                            setCredits((prev) =>
                              prev.map((d, i) => (i === index ? { ...d, amount: formatAmount(totalAmount, null) } : d))
                            );
                          }
                        });
                      }}
                      className={styles.memberLabel}
                    >
                      Members ({rowMembers.find((r) => r.accountId === credit.accountId)?.members.length || 0})
                    </Button>
                  </Grid>
                )}
                <Grid item xs={12} sm={1}>
                  {credits.length > 1 && (
                    <Button className={styles.remove} link onClick={() => removeCreditRow(index)} tiny>
                      <CloseIcon />
                    </Button>
                  )}
                </Grid>
              </Grid>
            ) : (
              <CircularProgress className={styles.circle} />
            )
          )}

          <Button link startIcon={<AddIcon />} onClick={addCreditRow}>
            Add Credit
          </Button>

          <br />
          <br />

          {!fields.isGeneralJournal.value && isCashCredit && (
            <Grid container direction="row" spacing={2}>
              <Grid item xs={12} sm={6}>
                <Field {...fields.payee} required={isCashCredit}>
                  <TextField {...fields.payee} onChange={modifyField} onBlur={validateField} />
                </Field>
              </Grid>
            </Grid>
          )}

          <Grid container direction="row" spacing={2}>
            <Grid item xs={12} sm={6}>
              <Field {...fields.remarks}>
                <TextField {...fields.remarks} onChange={modifyField} onBlur={validateField} />
              </Field>
            </Grid>
          </Grid>

          <Grid container direction="row">
            <Grid item xs={12} sm={6}>
              <div className={styles.checkbox}>
                <Checkbox {...fields.isGeneralJournal} onChange={modifyField} checked={fields.isGeneralJournal.value} />
                <Text className={styles.gasText}>Is General Journal</Text>
              </div>
            </Grid>
          </Grid>
        </div>

        {shouldShowBalanceMessage && (
          <>
            <Text className={isBalanced ? styles.balanced : styles.unbalanced}>
              {isBalanced ? 'Debits and Credits are balanced' : 'Debits and Credits are NOT balanced'}
            </Text>
          </>
        )}

        <Grid container direction="row" spacing={2}>
          <Grid item xs={12} sm={6}>
            <ActionButton
              right
              items={[
                {
                  onClick: () => {
                    state.close();
                  },
                  disabled: createTransactionRequest.loading,
                  text: locale.cancel
                },
                {
                  onClick: () => {
                    submit();
                  },
                  disabled:
                    !isBalanced ||
                    !entriesAreComplete ||
                    !fields.remarks.value ||
                    !isTransactedAtValid ||
                    !orNumbersValid ||
                    !cashVoucherNumbersValid ||
                    !payeeValid,
                  loading: createTransactionRequest.loading,
                  text: 'Submit'
                }
              ]}
            />
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default TransactionEntryForm;
